import React, { useEffect, useRef, useState } from 'react';
import { ApiSortDirection } from '../../api';
import TransactionsTable from '../../common/transactions-table/TransactionsTable';
import { transactionApi } from '../../apis';
import {
  InternalApiAttributeType,
  InternalApiSearchOperator,
  InternalApiSortDirection,
  InternalApiTransactionsPage,
} from '../../api-internal';
import { SalvButton, Size } from '@salvapp/ui-components';
import { createSearchParams, useNavigate } from 'react-router';
import { InternalNavigation } from '../../navigation/helper';
import { SearchParamKeys } from '../../common/searchParamKeys';

interface TransactionListProps {
  personId: string;
}

interface TransactionSearchValues {
  query: string | undefined;
  orderBy?: string;
  direction?: InternalApiSortDirection;
}

export const Transactions = ({ personId }: TransactionListProps): React.JSX.Element | null => {
  const [transactionsPage, setTransactionsPage] = useState<InternalApiTransactionsPage>({
    content: [],
    number: 0,
    size: 20,
    total: 0,
  });
  const page = useRef(0);
  const [fetching, setFetching] = useState(false);
  const search = useRef<TransactionSearchValues>({
    query: undefined,
  });
  const navigate = useNavigate();

  const fetchTransactions = async () => {
    try {
      setFetching(true);
      const transactions = await transactionApi.findAllPersonTransactions({
        personId,
        page: page.current,
        size: transactionsPage.size,
        query: search.current.query,
        orderBy: search.current.orderBy,
        direction: search.current.direction,
      });
      setTransactionsPage(transactions);
    } catch {
      console.warn('Error fetching transactions');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchByFilter = async (filter: string | undefined) => {
    page.current = 0;
    search.current = { ...search.current, query: filter };
    await fetchTransactions();
  };

  const sortByAttribute = async (attribute: string, direction: ApiSortDirection, query: string | undefined) => {
    search.current = { ...search.current, query, orderBy: attribute, direction: InternalApiSortDirection[direction] };
    await fetchTransactions();
  };

  const fetchPage = async (newPage: number) => {
    page.current = newPage;
    await fetchTransactions();
  };

  return (
    <TransactionsTable
      fetching={fetching}
      setFetching={setFetching}
      sortByAttribute={sortByAttribute}
      onSearch={fetchByFilter}
      fetchPage={fetchPage}
      page={transactionsPage}
      toCustomer={false}
      actions={() => (
        <SalvButton
          variant='muted'
          size={Size.SMALL}
          label='View in advanced search'
          onClick={() =>
            navigate({
              pathname: InternalNavigation.transactionSearch,
              search: createSearchParams({
                [SearchParamKeys.attributeIdentifier]: 'personExternalId',
                [SearchParamKeys.attributeType]: InternalApiAttributeType.GENERAL,
                [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                [SearchParamKeys.query]: personId,
              }).toString(),
            })
          }
        />
      )}
    />
  );
};

export default Transactions;
